import { Link } from "react-router-dom";
import type { LinkMonitorRecord, LinkMonitorStatus } from "@/hooks/useAdminLinkMonitor";

interface Props {
  records: LinkMonitorRecord[];
  totalRecords: number;
  healthyCount: number;
  redirectedCount: number;
  isRunning: boolean;
  progress: { current: number; total: number };
  lastRunAt: number | null;
  nextRunAt: number | null;
  onRunNow: () => void;
}

const STATUS_CONFIG: Record<LinkMonitorStatus, { label: string; className: string; icon: string }> = {
  pending: {
    label: "Pendente",
    className: "bg-slate-100 text-slate-600",
    icon: "ri-time-line",
  },
  ok: {
    label: "OK",
    className: "bg-emerald-50 text-emerald-600",
    icon: "ri-checkbox-circle-line",
  },
  redirected: {
    label: "Redirecionado",
    className: "bg-sky-50 text-sky-600",
    icon: "ri-route-line",
  },
  missing: {
    label: "Sem link",
    className: "bg-red-50 text-red-600",
    icon: "ri-link-unlink-m",
  },
  invalid: {
    label: "Inválido",
    className: "bg-red-50 text-red-600",
    icon: "ri-error-warning-line",
  },
  broken: {
    label: "Quebrado",
    className: "bg-red-50 text-red-600",
    icon: "ri-close-circle-line",
  },
  timeout: {
    label: "Timeout",
    className: "bg-amber-50 text-amber-600",
    icon: "ri-timer-flash-line",
  },
  error: {
    label: "Erro",
    className: "bg-amber-50 text-amber-600",
    icon: "ri-alert-line",
  },
};

function formatDateTime(value: number | null) {
  if (!value) return "—";
  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(value);
}

function formatLatency(value: number | null) {
  if (value === null) return "—";
  return `${value} ms`;
}

export default function BrokenLinksReport({
  records,
  totalRecords,
  healthyCount,
  redirectedCount,
  isRunning,
  progress,
  lastRunAt,
  nextRunAt,
  onRunNow,
}: Props) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-4 gap-5">
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-red-50 text-red-600 mb-3">
            <i className="ri-link-unlink-m text-lg"></i>
          </div>
          <p className="text-2xl font-bold text-gray-900">{records.length}</p>
          <p className="text-sm font-medium text-gray-700 mt-0.5">Links com problema</p>
          <p className="text-xs text-gray-400 mt-0.5">Itens aguardando correção</p>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 mb-3">
            <i className="ri-checkbox-circle-line text-lg"></i>
          </div>
          <p className="text-2xl font-bold text-gray-900">{healthyCount}</p>
          <p className="text-sm font-medium text-gray-700 mt-0.5">Links saudáveis</p>
          <p className="text-xs text-gray-400 mt-0.5">Respondendo corretamente</p>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-sky-50 text-sky-600 mb-3">
            <i className="ri-route-line text-lg"></i>
          </div>
          <p className="text-2xl font-bold text-gray-900">{redirectedCount}</p>
          <p className="text-sm font-medium text-gray-700 mt-0.5">Redirecionados</p>
          <p className="text-xs text-gray-400 mt-0.5">Links ativos com redirecionamento</p>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-orange-50 text-orange-600 mb-3">
            <i className="ri-calendar-check-line text-lg"></i>
          </div>
          <p className="text-sm font-bold text-gray-900">{formatDateTime(lastRunAt)}</p>
          <p className="text-sm font-medium text-gray-700 mt-2">Última varredura</p>
          <p className="text-xs text-gray-400 mt-0.5">Próxima: {formatDateTime(nextRunAt)}</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="font-bold text-gray-900">Lista detalhada de links quebrados</h2>
            <p className="text-sm text-gray-500 mt-1">
              A varredura automática roda a cada 24 horas e também pode ser executada manualmente.
            </p>
          </div>
          <button
            onClick={onRunNow}
            disabled={isRunning || totalRecords === 0}
            className="px-4 py-2.5 rounded-xl bg-orange-500 text-white text-sm font-semibold hover:bg-orange-600 disabled:bg-gray-200 disabled:text-gray-400 transition-colors cursor-pointer whitespace-nowrap"
          >
            {isRunning ? (
              <span className="inline-flex items-center gap-2">
                <i className="ri-loader-4-line animate-spin"></i>
                Verificando...
              </span>
            ) : (
              <span className="inline-flex items-center gap-2">
                <i className="ri-radar-line"></i>
                Verificar agora
              </span>
            )}
          </button>
        </div>

        {isRunning && progress.total > 0 && (
          <div className="mt-5 rounded-2xl border border-orange-100 bg-orange-50 px-4 py-3">
            <div className="flex items-center justify-between text-sm text-orange-700 mb-2">
              <span className="font-semibold">Varredura em andamento</span>
              <span>{progress.current}/{progress.total}</span>
            </div>
            <div className="h-2 rounded-full bg-orange-100 overflow-hidden">
              <div
                className="h-full bg-orange-500 rounded-full transition-all"
                style={{ width: `${progress.total > 0 ? (progress.current / progress.total) * 100 : 0}%` }}
              ></div>
            </div>
          </div>
        )}

        {records.length === 0 ? (
          <div className="py-14 text-center">
            <div className="w-14 h-14 flex items-center justify-center rounded-full bg-emerald-50 text-emerald-600 mx-auto mb-4">
              <i className="ri-checkbox-circle-line text-3xl"></i>
            </div>
            <p className="text-base font-semibold text-gray-800">Nenhum link quebrado no relatório</p>
            <p className="text-sm text-gray-500 mt-1">
              Conforme você corrige os itens, eles desaparecem automaticamente desta lista.
            </p>
          </div>
        ) : (
          <div className="mt-6 space-y-4">
            {records.map((record) => {
              const status = STATUS_CONFIG[record.status];
              return (
                <div key={record.key} className="rounded-2xl border border-gray-100 bg-gray-50/70 p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${status.className}`}>
                          <i className={status.icon}></i>
                          {status.label}
                        </span>
                        <span className="inline-flex items-center gap-1 rounded-full bg-white px-3 py-1 text-xs font-semibold text-gray-600 border border-gray-200">
                          <i className={record.itemType === "product" ? "ri-shopping-bag-3-line" : "ri-fire-line"}></i>
                          {record.itemType === "product" ? "Produto" : "Oferta do Dia"}
                        </span>
                        <span className="inline-flex items-center gap-1 rounded-full bg-white px-3 py-1 text-xs font-semibold text-gray-600 border border-gray-200">
                          <i className="ri-store-2-line"></i>
                          {record.marketplace}
                        </span>
                      </div>

                      <h3 className="text-base font-bold text-gray-900">{record.itemName}</h3>
                      <p className="text-sm text-gray-600 mt-1">{record.detail}</p>
                    </div>

                    <Link
                      to={record.editPath}
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-orange-200 text-sm font-semibold text-orange-600 hover:bg-orange-50 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-edit-line"></i>
                      Corrigir cadastro
                    </Link>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mt-5 text-sm">
                    <div className="rounded-xl bg-white border border-gray-100 p-4">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">Link cadastrado</p>
                      {record.link ? (
                        <a
                          href={record.link}
                          target="_blank"
                          rel="nofollow noopener noreferrer"
                          className="text-orange-600 hover:underline break-all"
                        >
                          {record.link}
                        </a>
                      ) : (
                        <p className="text-gray-400 italic">Não informado</p>
                      )}
                    </div>
                    <div className="rounded-xl bg-white border border-gray-100 p-4">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">Destino final verificado</p>
                      {record.finalUrl ? (
                        <a
                          href={record.finalUrl}
                          target="_blank"
                          rel="nofollow noopener noreferrer"
                          className="text-gray-700 hover:text-orange-600 hover:underline break-all"
                        >
                          {record.finalUrl}
                        </a>
                      ) : (
                        <p className="text-gray-400 italic">Não disponível</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-4 gap-4 mt-4">
                    <div className="rounded-xl bg-white border border-gray-100 p-4">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1.5">HTTP</p>
                      <p className="text-sm font-semibold text-gray-800">{record.httpStatus ?? "—"}</p>
                    </div>
                    <div className="rounded-xl bg-white border border-gray-100 p-4">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1.5">Método</p>
                      <p className="text-sm font-semibold text-gray-800">{record.checkMethod ?? "—"}</p>
                    </div>
                    <div className="rounded-xl bg-white border border-gray-100 p-4">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1.5">Tempo de resposta</p>
                      <p className="text-sm font-semibold text-gray-800">{formatLatency(record.latencyMs)}</p>
                    </div>
                    <div className="rounded-xl bg-white border border-gray-100 p-4">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1.5">Última checagem</p>
                      <p className="text-sm font-semibold text-gray-800">{formatDateTime(record.checkedAt)}</p>
                    </div>
                  </div>

                  {record.history.length > 0 && (
                    <div className="mt-4 rounded-xl bg-white border border-gray-100 p-4">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">Histórico recente</p>
                      <div className="space-y-2">
                        {record.history.slice(0, 3).map((entry, index) => {
                          const historyStatus = STATUS_CONFIG[entry.status];
                          return (
                            <div key={`${record.key}-${entry.checkedAt}-${index}`} className="flex items-center justify-between gap-4 text-sm">
                              <div className="flex items-center gap-2 min-w-0">
                                <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold ${historyStatus.className}`}>
                                  <i className={historyStatus.icon}></i>
                                  {historyStatus.label}
                                </span>
                                <span className="text-gray-500 truncate">{entry.detail}</span>
                              </div>
                              <span className="text-gray-400 whitespace-nowrap">{formatDateTime(entry.checkedAt)}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
